import { CreditCard, Banknote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

/**
 * Renders available payment method options based on restaurant configuration.
 * Handles: online (KaisoPay/Viva/Stripe), cash, card at restaurant, Bizum, MBWay.
 *
 * ETAPA 10: local gateway methods (Apple Pay, Mada, STC Pay) are shown as
 * informational badges inside the ONLINE option when the restaurant has
 * local_payment_methods_enabled=true and lists them in enabled_payment_methods.
 * They are NOT separate checkout flows — all go through the same gateway session;
 * Stripe surfaces the available methods on its hosted page automatically.
 */
export default function CheckoutPaymentMethods({ restaurant, tc, fmt, cartTotal, payment, onPaymentChange, isTableOrder, table, couponPolicy }) {
  const { t } = useTranslation();
  const [pr, pg, pb] = tc.primary.replace("#","").match(/.{2}/g).map(h => parseInt(h,16));

  const hasGateways = restaurant?.payment_gateways?.length > 0;
  const kaisopayEnabled = restaurant?.kaisopay_enabled === true;
  const kaisopayReady = kaisopayEnabled && restaurant?.kaisopay_onboarding_complete !== false;
  const hasNonKaisopayGateway = (restaurant?.payment_gateways || []).some(g => g !== "kaisopay");
  const onlineAvailable = hasGateways && (hasNonKaisopayGateway || kaisopayReady);
  const onlineOnly = couponPolicy?.valid && couponPolicy?.online_only === true;
  const paymentMethods = restaurant?.payment_methods || [];

  // ETAPA 10: local gateway methods — only shown when explicitly enabled per tenant.
  const enabledPMs = restaurant?.enabled_payment_methods || ["card"];
  const showApplePay = enabledPMs.includes("apple_pay");
  const showMada     = enabledPMs.includes("mada");
  const showStcPay   = enabledPMs.includes("stc_pay");

  const activeStyle = (active) => active
    ? { borderColor: tc.primary, backgroundColor: `rgba(${pr},${pg},${pb},0.06)` }
    : {};
  const baseClass = (active) =>
    `flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${active ? "" : "border-gray-200 bg-gray-50"}`;

  return (
    <div>
      <Label className="text-xs font-semibold uppercase" style={{ color: tc.primary }}>{t("checkout.paymentMethodLabel")}</Label>
      <div className="space-y-2 mt-1.5">

        {/* Online */}
        {onlineAvailable && (
          <div onClick={() => onPaymentChange.method("online")}
            className={baseClass(payment.method === "online")}
            style={activeStyle(payment.method === "online")}
            data-testid="payment-online">
            <CreditCard className="w-5 h-5" style={{ color: payment.method === "online" ? tc.primary : "#9ca3af" }} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">ONLINE</span>
                {kaisopayReady && <span className="text-[9px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.5 rounded-full" data-testid="kaisopay-badge">KaisoPay</span>}
                {restaurant?.payment_gateways?.includes("viva") && <span className="text-[10px] text-gray-500">via Viva Wallet</span>}
                {/* ETAPA 10: local gateway method badges — informational only */}
                {showApplePay && (
                  <span className="text-[9px] bg-gray-100 text-gray-700 font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-0.5" data-testid="apple-pay-badge">
                    <svg viewBox="0 0 14 14" className="w-3 h-3" fill="currentColor"><path d="M9.36 2C9.14 2.56 8.79 3.07 8.36 3.44c-.43.37-.98.61-1.56.56-.08-.58.15-1.2.5-1.6.36-.42.96-.74 1.53-.79.09.56-.06 1.09-.47 1.39Z"/><path d="M11.88 9.77c-.27.6-.58 1.15-.96 1.63-.52.67-1.04 1.34-1.89 1.35-.81.01-1.08-.5-2.02-.5-.93 0-1.22.49-2.01.51-.83.02-1.37-.71-1.9-1.37-.53-.67-1.01-1.72-1.25-2.88-.23-1.13-.11-2.27.38-3.22.37-.72.9-1.28 1.5-1.61.61-.34 1.27-.46 1.93-.36.73.11 1.38.5 1.88.5.48 0 1.4-.56 2.3-.47.39.02 1.47.16 2.17 1.19-.06.04-1.3.75-1.28 2.24.01 1.77 1.56 2.36 1.15 3.99Z"/></svg>
                    Apple Pay
                  </span>
                )}
                {showMada && (
                  <span className="text-[9px] bg-green-50 text-green-700 font-semibold px-1.5 py-0.5 rounded-full" data-testid="mada-badge">
                    mada
                  </span>
                )}
                {showStcPay && (
                  <span className="text-[9px] bg-purple-50 text-purple-700 font-semibold px-1.5 py-0.5 rounded-full" data-testid="stc-pay-badge">
                    STC Pay
                  </span>
                )}
              </div>
              {kaisopayReady && payment.method === "online" && (
                <p className="text-[10px] text-emerald-600 mt-0.5" data-testid="kaisopay-info">{t("checkout.stripeSecure")}</p>
              )}
            </div>
          </div>
        )}

        {/* Cash */}
        {!onlineOnly && paymentMethods.some(m => m.name?.toLowerCase().includes("efectivo") && m.active !== false && m.delivery === true && !m.pos_only) && (
          <div onClick={() => onPaymentChange.method("cash")}
            className={baseClass(payment.method === "cash")}
            style={activeStyle(payment.method === "cash")}
            data-testid="payment-cash">
            <Banknote className="w-5 h-5" style={{ color: payment.method === "cash" ? tc.primary : "#9ca3af" }} />
            <span className="text-sm font-medium">{t("checkout.cashMethod")}</span>
          </div>
        )}

        {/* Card at restaurant */}
        {!onlineOnly && paymentMethods.some(m => (m.name?.toLowerCase().includes("visa") || m.name?.toLowerCase().includes("mastercard") || m.name?.toLowerCase().includes("tarjeta")) && m.active !== false && m.delivery === true && !m.pos_only) && (
          <div onClick={() => onPaymentChange.method("card_restaurant")}
            className={baseClass(payment.method === "card_restaurant")}
            style={activeStyle(payment.method === "card_restaurant")}
            data-testid="payment-card-restaurant">
            <CreditCard className="w-5 h-5" style={{ color: payment.method === "card_restaurant" ? tc.primary : "#9ca3af" }} />
            <div>
              <span className="text-sm font-medium">{t("checkout.cardMethod")}</span>
              <span className="text-[10px] text-gray-500 block">
                {paymentMethods.filter(m => (m.name?.toLowerCase().includes("visa") || m.name?.toLowerCase().includes("mastercard") || m.name?.toLowerCase().includes("tarjeta")) && m.active !== false && m.delivery === true && !m.pos_only).map(m => m.name).join(", ")}
              </span>
            </div>
          </div>
        )}

        {/* Bizum */}
        {!onlineOnly && paymentMethods.some(m => m.name?.toLowerCase().includes("bizum") && m.active !== false && m.delivery === true && !m.pos_only) && (
          <div onClick={() => onPaymentChange.method("bizum")}
            className={baseClass(payment.method === "bizum")}
            style={payment.method === "bizum" ? { borderColor: "#05C3DD", backgroundColor: "rgba(5,195,221,0.06)" } : {}}
            data-testid="payment-bizum">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><rect width="24" height="24" rx="4" fill="#05C3DD"/><text x="12" y="16" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="800" fontFamily="Arial,sans-serif">Biz</text></svg>
            <span className="text-sm font-medium">BIZUM</span>
          </div>
        )}

        {/* MBWay */}
        {!onlineOnly && paymentMethods.some(m => m.name?.toLowerCase().includes("mbway") && m.active !== false && m.delivery === true && !m.pos_only) && (
          <div onClick={() => onPaymentChange.method("mbway")}
            className={baseClass(payment.method === "mbway")}
            style={payment.method === "mbway" ? { borderColor: "#E42313", backgroundColor: "rgba(228,35,19,0.06)" } : {}}
            data-testid="payment-mbway">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><rect width="24" height="24" rx="4" fill="#E42313"/><text x="12" y="16" textAnchor="middle" fill="#fff" fontSize="6" fontWeight="800" fontFamily="Arial,sans-serif">MB</text></svg>
            <span className="text-sm font-medium">MBWAY</span>
          </div>
        )}

        {/* Fallback: no gateways or methods configured */}
        {!onlineOnly && !hasGateways && !paymentMethods.length && (
          <div onClick={() => onPaymentChange.method("cash")}
            className={baseClass(payment.method === "cash")}
            style={activeStyle(payment.method === "cash")}
            data-testid="payment-cash-fallback">
            <Banknote className="w-5 h-5" style={{ color: payment.method === "cash" ? tc.primary : "#9ca3af" }} />
            <span className="text-sm font-medium">{t("checkout.cashMethod")}</span>
          </div>
        )}
      </div>
      {onlineOnly && (
        <p className="text-xs text-emerald-700 mt-2 font-medium" data-testid="coupon-online-lock">
          Esta oferta exige pagamento online.
        </p>
      )}

      {/* Cash change */}
      {payment.method === "cash" && (
        <div className="mt-2">
          <Label className="text-xs text-gray-600">{t("checkout.changeForLabel")}</Label>
          <Input type="number" step="0.01" value={payment.changeFor}
            onChange={e => onPaymentChange.changeFor(e.target.value)}
            placeholder={`Ex: ${Math.ceil(cartTotal / 5) * 5}`}
            className="bg-gray-50 mt-1" data-testid="change-for-input" />
          {payment.changeFor && !isNaN(Number(payment.changeFor)) && Number(payment.changeFor) > cartTotal && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="change-display-inline">
              <span className="text-sm text-amber-700 font-semibold">{t("checkout.change")}: </span>
              <span className="font-bold text-lg text-amber-800">{fmt(Number(payment.changeFor) - cartTotal)}</span>
            </div>
          )}
          {payment.changeFor && !isNaN(Number(payment.changeFor)) && Number(payment.changeFor) > 0 && Number(payment.changeFor) <= cartTotal && (
            <p className="text-xs text-red-500 mt-1" data-testid="change-insufficient">{t("checkout.changeInvalid")} ({fmt(cartTotal)})</p>
          )}
        </div>
      )}

      {/* Bizum info */}
      {payment.method === "bizum" && (
        <div className="mt-2 bg-[#05C3DD]/5 border border-[#05C3DD]/20 rounded-lg p-3" data-testid="bizum-info">
          <p className="text-sm font-semibold text-[#05C3DD] mb-1">{t("checkout.bizumPayment")}</p>
          {restaurant?.reservation_settings?.bizum_phone ? (
            <>
              <p className="text-sm text-gray-700">Envie <strong>{fmt(cartTotal)}</strong> por Bizum para:</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{restaurant.reservation_settings.bizum_phone}</p>
              <p className="text-xs text-gray-500 mt-1">{t("checkout.bizumConfirm")}</p>
            </>
          ) : (
            <p className="text-sm text-gray-600">{t("checkout.bizumAtPickup")}</p>
          )}
        </div>
      )}

      {/* MBWay info */}
      {payment.method === "mbway" && (
        <div className="mt-2 bg-[#E42313]/5 border border-[#E42313]/20 rounded-lg p-3" data-testid="mbway-info">
          <p className="text-sm font-semibold text-[#E42313]">{t("checkout.mbwayAtPickup")}</p>
        </div>
      )}

      {/* Card at restaurant info */}
      {payment.method === "card_restaurant" && (
        <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="card-restaurant-info">
          <p className="text-sm text-gray-600">El pago se realizará al recibir tu pedido.</p>
        </div>
      )}
    </div>
  );
}
